import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';
import Popover from 'react-bootstrap/lib/Popover';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import cookie from 'react-cookie';

import config from 'config';

import ButtonGroup from 'react-bootstrap/lib/ButtonGroup';
import Button from 'react-bootstrap/lib/Button';
import Col from 'react-bootstrap/lib/Col';
import Grid from 'react-bootstrap/lib/Grid';
import Row from 'react-bootstrap/lib/Row';

import { loadExternalScript } from 'helpers/HelperFunctions';
import {
  getSubscriptionToken,
  getApplicationCreditsCheckoutToken,
} from 'redux/modules/payments';
import { load as loadSubscriptionPortalUrl } from 'redux/modules/subscriptions';
import { setErrorMessage } from 'redux/modules/errorMessage';

import ButtonWithLoader from 'components/ButtonWithLoader/ButtonWithLoader';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import CurrencySelector from 'components/Shared/Selectors/CurrencySelector';

import styles from './PricingBreakdownCreative.module.scss';
import PaygPricingSection from './PaygPricingSection';

import usePrevious from 'hooks/usePrevious';
import useApplicationCreditsCheckout from 'hooks/useApplicationCreditsCheckout';
import { RECENT_JOBS_COOKIE } from 'constants/constants';
import {
  isBlackFridayEligible,
  getBlackFridayCountdown,
} from 'helpers/BlackFridayHelper';
import { resolveSubscriptionLogJobId } from 'helpers/maybeLogProSubscriptionTransaction';
import { storePreCheckoutAudience, AUDIENCES } from 'helpers/audienceTracking';
import { fetchCelloNewUserRewardEligible } from 'helpers/celloReferral';
import {
  getApplicationCreditsPerApplication,
  getApplicationCreditsPrice,
  getProAnnualPerApplication,
  getProAnnualSavingsMultiplier,
  isPaygCreditsPricingEnabled,
  PRICING_CREATIVE_PATH,
} from 'helpers/ApplicationCreditsHelper';

const SUBSCRIPTION_ALREADY_SUBSCRIBED_MESSAGE =
  'You are already subscribed, redirecting you to manage your subscription';

const CHECKOUT_REDIRECT_TIMEOUT_MS = 15000;
const CHECKOUT_ERROR_MESSAGE =
  'Unable to start checkout. Please refresh the page and try again.';

const getCheckoutSuccessUrl = (param) => {
  if (typeof location === 'undefined') return '';
  const recentJobs = cookie.load(RECENT_JOBS_COOKIE) || [];
  const applyBriefLink = cookie.load('brief-link') || '';
  if (recentJobs.length > 0) {
    return `${location.origin}${recentJobs[0].url}?${param}`;
  }
  if (applyBriefLink) {
    return `${location.origin}${applyBriefLink}?${param}`;
  }
  return `${location.origin}/me?${param}`;
};

const PricingBreakdownCreative = ({
  className,
  getSubscriptionToken,
  getApplicationCreditsCheckoutToken,
  gradient,
  submitId,
  submitTextPro,
  submitTextPremium,
  submitLoadingText,
  user,
  subscriptionToken,
  applicationCreditsCheckoutToken,
  loadingApplicationCreditsCheckout,
  applicationCreditsCheckoutError,
  premiumSubmitId,
  ...rest
}) => {
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [billing, setBilling] = useState('year');
  const [product, setProduct] = useState('pro');
  const [currency, setCurrency] = useState({ label: 'USD ($)', value: '$' });
  const [loadingPro, setLoadingPro] = useState(false);
  const [loadingPremium, setLoadingPremium] = useState(false);
  const [stripe, setStripe] = useState(undefined);
  const [shouldGetToken, setShouldGetToken] = useState(false);
  const [pendingCheckoutSessionId, setPendingCheckoutSessionId] =
    useState(null);
  const [blackFridayCountdown, setBlackFridayCountdown] = useState(
    getBlackFridayCountdown()
  );
  /** null = not checked yet; true only after Cello eligible (requires cello-referral cookie first). */
  const [celloReferralValid, setCelloReferralValid] = useState(null);
  const checkoutTimeoutRef = useRef(null);
  const checkoutRedirectInFlightRef = useRef(false);
  const environment =
    process.env.ENDPOINT_FRONTEND === 'www.twine.net'
      ? 'production'
      : 'development';
  const showPaygOption = isPaygCreditsPricingEnabled(user);
  const isPaygBilling = billing === 'payg';
  const applicationCreditsPrice = getApplicationCreditsPrice(currency);
  const applicationCreditsPerApplication =
    getApplicationCreditsPerApplication(currency);

  const { subscriptionUrl } = useSelector((state) => state.subscriptions);

  const clearCheckoutLoading = useCallback(() => {
    setLoadingPro(false);
    setLoadingPremium(false);
  }, []);

  const failCheckout = useCallback(() => {
    clearCheckoutLoading();
    setPendingCheckoutSessionId(null);
    checkoutRedirectInFlightRef.current = false;
    if (checkoutTimeoutRef.current) {
      clearTimeout(checkoutTimeoutRef.current);
      checkoutTimeoutRef.current = null;
    }
    dispatch(setErrorMessage(CHECKOUT_ERROR_MESSAGE));
  }, [clearCheckoutLoading, dispatch]);

  // Set default currency to GBP for UK users
  useEffect(() => {
    if (user?.location?.text?.includes('United Kingdom')) {
      setCurrency({ label: 'GBP (£)', value: '£' });
    }
  }, [user]);

  useEffect(() => {
    if (!showPaygOption && billing === 'payg') {
      setBilling('year');
    }
  }, [showPaygOption, billing]);

  useEffect(() => {
    if (typeof stripe === 'undefined') {
      loadExternalScript('https://js.stripe.com/v3/', [], () => initStripe());
    }

    if (user?.subscribed) {
      dispatch(loadSubscriptionPortalUrl());
    }
  }, []);

  const redirectToStripeCheckout = useCallback(
    (sessionId, { onError } = {}) => {
      if (!sessionId) {
        return;
      }

      // Queue until Stripe.js is ready (avoids silent no-op when token wins the race).
      if (!stripe) {
        setPendingCheckoutSessionId(sessionId);
        return;
      }

      (async () => {
        try {
          const result = await stripe.redirectToCheckout({ sessionId });
          if (result?.error) {
            console.warn('Error:', result.error);
            onError?.();
          }
        } catch (err) {
          console.error('Stripe redirect error:', err);
          onError?.();
        }
      })();
    },
    [stripe]
  );

  const creditsCheckoutUrls = useMemo(
    () => ({
      success_url: getCheckoutSuccessUrl('payment-credits-success'),
      cancel_url:
        typeof location !== 'undefined'
          ? `${location.origin}${PRICING_CREATIVE_PATH}?payment-credits-failure`
          : '',
    }),
    []
  );

  const { redirectToCreditsCheckout } = useApplicationCreditsCheckout({
    user,
    currency,
    getApplicationCreditsCheckoutToken,
    applicationCreditsCheckoutToken,
    applicationCreditsCheckoutError,
    redirectToStripeCheckout,
    checkoutUrls: creditsCheckoutUrls,
  });

  const prevSubscriptionToken = usePrevious(subscriptionToken);
  useEffect(() => {
    if (
      subscriptionToken !== '' &&
      subscriptionToken !== prevSubscriptionToken
    ) {
      checkoutRedirectInFlightRef.current = false;
      setPendingCheckoutSessionId(subscriptionToken);
    }
  }, [subscriptionToken, prevSubscriptionToken]);

  // Redirect once both a session id and Stripe.js are available.
  useEffect(() => {
    if (
      !pendingCheckoutSessionId ||
      !stripe ||
      checkoutRedirectInFlightRef.current
    ) {
      return undefined;
    }

    checkoutRedirectInFlightRef.current = true;
    const sessionId = pendingCheckoutSessionId;

    (async () => {
      try {
        const result = await stripe.redirectToCheckout({ sessionId });
        if (result?.error) {
          console.warn('Error:', result.error);
          failCheckout();
        }
      } catch (err) {
        console.error('Stripe redirect error:', err);
        failCheckout();
      }
    })();

    return undefined;
  }, [pendingCheckoutSessionId, stripe, failCheckout]);

  // Safety net: never leave the button stuck on Loading if Stripe.js is blocked
  // or redirect never completes.
  useEffect(() => {
    if (!loadingPro && !loadingPremium) {
      if (checkoutTimeoutRef.current) {
        clearTimeout(checkoutTimeoutRef.current);
        checkoutTimeoutRef.current = null;
      }
      return undefined;
    }

    checkoutTimeoutRef.current = setTimeout(() => {
      failCheckout();
    }, CHECKOUT_REDIRECT_TIMEOUT_MS);

    return () => {
      if (checkoutTimeoutRef.current) {
        clearTimeout(checkoutTimeoutRef.current);
        checkoutTimeoutRef.current = null;
      }
    };
  }, [loadingPro, loadingPremium, failCheckout]);

  useEffect(() => {
    if (shouldGetToken) {
      getToken(getSubscription());
      setShouldGetToken(false);
    }
  }, [shouldGetToken, product]);

  // Reset loading states when component unmounts or user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      setLoadingPro(false);
      setLoadingPremium(false);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      setLoadingPro(false);
      setLoadingPremium(false);
      if (checkoutTimeoutRef.current) {
        clearTimeout(checkoutTimeoutRef.current);
      }
    };
  }, []);

  // Update Black Friday countdown every second
  useEffect(() => {
    if (!isBlackFridayEligible(user)) {
      return;
    }

    const timer = setInterval(() => {
      setBlackFridayCountdown(getBlackFridayCountdown());
    }, 1000);

    return () => clearInterval(timer);
  }, [user]);

  useEffect(() => {
    if (!user?.id) {
      setCelloReferralValid(false);
      return undefined;
    }
    let cancelled = false;
    fetchCelloNewUserRewardEligible(user.id).then((ok) => {
      if (!cancelled) {
        setCelloReferralValid(ok);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const initStripe = () => {
    if (typeof stripe === 'undefined') {
      try {
        if (environment === 'production') {
          setStripe(window.Stripe(config.stripeKey));
        } else {
          setStripe(window.Stripe(config.stripeDevKey));
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  const redirectToCheckout = (event) => {
    if (!user) {
      window.location.href = `/signin?referer=${PRICING_CREATIVE_PATH}`;
    } else {
      try {
        // Set product type based on button ID
        const buttonId = event.target.id;
        if (buttonId === premiumSubmitId) {
          setProduct('premium');
          setLoadingPremium(true);
        } else if (buttonId === submitId) {
          setProduct('pro');
          setLoadingPro(true);
        }

        // Set flag to get token after product state updates
        setShouldGetToken(true);
      } catch (err) {
        console.error(err);
        setLoadingPro(false);
        setLoadingPremium(false);
      }

      event.preventDefault();
    }
  };

  const getSubscriptionUrl = () => {
    if (subscriptionUrl) {
      return subscriptionUrl;
    }

    return `https://billing.stripe.com/p/login/4gw5kmbsI0Cb1q0cMM?prefilled_email=${encodeURIComponent(
      user.email
    )}`;
  };

  const getPrice = () => {
    const DAYS_PER_MONTH = 30;
    const isBlackFriday = isBlackFridayEligible(user);
    const hasValidatedCelloReferral = celloReferralValid === true;

    if (currency.value === '$') {
      const baseMonthPrice = 18.99;
      const basePremiumMonthPrice = 41.99;
      const baseYearPrice = 227.88; // Full year price (18.99 * 12)
      const basePremiumYearPrice = 503.88; // Full year price (41.99 * 12)

      const basePrice = {
        // Monthly prices
        month_no_discount: baseMonthPrice.toFixed(2),
        month: hasValidatedCelloReferral
          ? (baseMonthPrice * 0.85).toFixed(2) // 15% Cello referral (takes precedence over BF)
          : isBlackFriday
          ? (baseMonthPrice * 0.9).toFixed(2) // 10% off for Black Friday
          : baseMonthPrice.toFixed(2),
        premium_month_no_discount: basePremiumMonthPrice.toFixed(2),
        premium_month: hasValidatedCelloReferral
          ? (basePremiumMonthPrice * 0.85).toFixed(2)
          : isBlackFriday
          ? (basePremiumMonthPrice * 0.9).toFixed(2) // 10% off for Black Friday
          : basePremiumMonthPrice.toFixed(2),

        // Annual prices
        year_no_discount: baseYearPrice.toFixed(2),
        year: isBlackFriday
          ? (baseYearPrice * 0.45).toFixed(2) // 55% off for Black Friday
          : 113.93, // 50% off normally
        premium_year_no_discount: basePremiumYearPrice.toFixed(2),
        premium_year: isBlackFriday
          ? (basePremiumYearPrice * 0.45).toFixed(2) // 55% off for Black Friday
          : 249.99, // 50% off normally

        // Monthly equivalent of annual
        year_month: isBlackFriday
          ? ((baseYearPrice * 0.45) / 12).toFixed(2)
          : 9.49,
        premium_year_month: isBlackFriday
          ? ((basePremiumYearPrice * 0.45) / 12).toFixed(2)
          : 20.83,
      };

      return {
        ...basePrice,
        month_daily: (parseFloat(basePrice.month) / DAYS_PER_MONTH).toFixed(2),
        premium_month_daily: (
          parseFloat(basePrice.premium_month) / DAYS_PER_MONTH
        ).toFixed(2),
        premium_year_month_daily: (
          parseFloat(basePrice.premium_year_month) / DAYS_PER_MONTH
        ).toFixed(2),
        year_month_daily: (
          parseFloat(basePrice.year_month) / DAYS_PER_MONTH
        ).toFixed(2),
      };
    }

    if (currency.value === '£') {
      const baseMonthPrice = 14.99;
      const basePremiumMonthPrice = 31.99;
      const baseYearPrice = 179.88; // Full year price (14.99 * 12)
      const basePremiumYearPrice = 383.88; // Full year price (31.99 * 12)

      const basePrice = {
        // Monthly prices
        month_no_discount: baseMonthPrice.toFixed(2),
        month: hasValidatedCelloReferral
          ? (baseMonthPrice * 0.85).toFixed(2)
          : isBlackFriday
          ? (baseMonthPrice * 0.9).toFixed(2) // 10% off for Black Friday
          : baseMonthPrice.toFixed(2),
        premium_month_no_discount: basePremiumMonthPrice.toFixed(2),
        premium_month: hasValidatedCelloReferral
          ? (basePremiumMonthPrice * 0.85).toFixed(2)
          : isBlackFriday
          ? (basePremiumMonthPrice * 0.9).toFixed(2) // 10% off for Black Friday
          : basePremiumMonthPrice.toFixed(2),

        // Annual prices
        year_no_discount: baseYearPrice.toFixed(2),
        year: isBlackFriday
          ? (baseYearPrice * 0.45).toFixed(2) // 55% off for Black Friday
          : 89.93, // 50% off normally
        premium_year_no_discount: basePremiumYearPrice.toFixed(2),
        premium_year: isBlackFriday
          ? (basePremiumYearPrice * 0.45).toFixed(2) // 55% off for Black Friday
          : 189.99, // 50% off normally

        // Monthly equivalent of annual
        year_month: isBlackFriday
          ? ((baseYearPrice * 0.45) / 12).toFixed(2)
          : 7.49,
        premium_year_month: isBlackFriday
          ? ((basePremiumYearPrice * 0.45) / 12).toFixed(2)
          : 15.83,
      };

      return {
        ...basePrice,
        month_daily: (parseFloat(basePrice.month) / DAYS_PER_MONTH).toFixed(2),
        premium_month_daily: (
          parseFloat(basePrice.premium_month) / DAYS_PER_MONTH
        ).toFixed(2),
        premium_year_month_daily: (
          parseFloat(basePrice.premium_year_month) / DAYS_PER_MONTH
        ).toFixed(2),
        year_month_daily: (
          parseFloat(basePrice.year_month) / DAYS_PER_MONTH
        ).toFixed(2),
      };
    }
  };

  const getCurrency = () => {
    if (currency.value === '$') {
      return { code: 'usd', symbol: '$' };
    }

    if (currency.value === '£') {
      return { code: 'gbp', symbol: '£' };
    }
  };

  const getSubscription = () => {
    const currencyCode = getCurrency().code;
    const productConfig =
      environment === 'production'
        ? config.stripeProducts
        : config.stripeProductsDev;
    const productType = productConfig[product][currencyCode];

    return billing === 'year' ? productType.year : productType.month;
  };

  const getToken = (subscription) => {
    const isBlackFriday = isBlackFridayEligible(user);
    let coupon_id = null;

    // Black Friday coupon codes (55% off annual only)
    if (
      isBlackFriday &&
      billing === 'year' &&
      (product === 'pro' || product === 'premium')
    ) {
      coupon_id =
        environment === 'production'
          ? config.stripeProducts.coupons.blackFriday[product][
              getCurrency().code
            ].year
          : config.stripeProductsDev.coupons.blackFriday[product][
              getCurrency().code
            ].year;
    }
    // Regular 50% off annual Pro coupon (non-Black Friday)
    else if (billing === 'year' && product === 'pro') {
      coupon_id =
        environment === 'production'
          ? config.stripeProducts.coupons.pro[getCurrency().code].year
          : config.stripeProductsDev.coupons.pro[getCurrency().code].year;
    }
    // Monthly Pro/Premium when Cello attribution cookie is set (`cello-referral`).
    // Coupon ids: config stripeProducts*.coupons.celloReferral.month
    else if (
      billing === 'month' &&
      (product === 'pro' || product === 'premium') &&
      celloReferralValid === true
    ) {
      coupon_id =
        environment === 'production'
          ? config.stripeProducts.coupons.celloReferral.month[
              getCurrency().code
            ]
          : config.stripeProductsDev.coupons.celloReferral.month[
              getCurrency().code
            ];
    }

    const jobId = resolveSubscriptionLogJobId();
    const tokenRequestObject = {
      user_id: user.id,
      plan_id: subscription,
      ...(coupon_id && { coupon_id }), // Only add coupon_id if it exists
      ...(jobId != null && { job_id: jobId }),
      success_url: getCheckoutSuccessUrl('success'),
      cancel_url: `${location.origin}/pricing/creative?cancel`,
    };

    storePreCheckoutAudience({
      userId: user.id,
      audience: user.audience || AUDIENCES.FREELANCER_REGISTERED_NOT_PRO_30D,
    });

    getSubscriptionToken(tokenRequestObject)
      .then((response) => {
        console.log('Subscription token response:', response);
      })
      .catch((error) => {
        const errorMessage = error?.errors?.message?.[0] || '';

        if (errorMessage === SUBSCRIPTION_ALREADY_SUBSCRIBED_MESSAGE) {
          setTimeout(() => {
            window.location.href = getSubscriptionUrl();
          }, 3000);
          return;
        }

        // Reset loading states on error
        setLoadingPro(false);
        setLoadingPremium(false);

        // Check for currency mismatch in error response
        const isCurrencyMismatch =
          errorMessage ===
            'The currency of the selected prices (gbp) does not match the customer currency (usd).' ||
          errorMessage ===
            'The currency of the selected prices (usd) does not match the customer currency (gbp).';
        if (isCurrencyMismatch) {
          // Only log info level for currency mismatch since we're handling it
          console.log(
            'Currency mismatch detected, switching currency and retrying...'
          );

          // Extract the required currency from error message
          const requiredCurrency = errorMessage.includes(
            'customer currency (gbp)'
          )
            ? 'gbp'
            : 'usd';
          const newCurrency =
            requiredCurrency === 'gbp'
              ? { label: 'GBP (£)', value: '£' }
              : { label: 'USD ($)', value: '$' };

          // Store current product type and billing cycle
          const currentProduct = product; // 'pro' or 'premium'
          const currentBilling = billing; // 'year' or 'month'

          // Update currency state
          setCurrency(newCurrency);

          // Wait for state update and retry with new subscription plan
          setTimeout(() => {
            // Get new subscription plan ID with updated currency but same product type
            const productConfig =
              environment === 'production'
                ? config.stripeProducts
                : config.stripeProductsDev;
            const newPlanId =
              productConfig[currentProduct][requiredCurrency][currentBilling];
            console.log('Retrying with new subscription plan:', {
              product: currentProduct,
              currency: requiredCurrency,
              billing: currentBilling,
              planId: newPlanId,
            });
            getToken(newPlanId);
          }, 100);
        } else {
          // Log other errors as actual errors
          console.error('Subscription token error details:', error);
        }
      });
  };

  const hasCelloReferral = celloReferralValid === true;
  const price = getPrice();
  const proAnnualPerApplication = getProAnnualPerApplication(price.year);
  const proAnnualSavingsMultiplier = getProAnnualSavingsMultiplier(
    applicationCreditsPerApplication,
    price.year
  );
  const hoursRemaining = Math.floor(
    (new Date().setHours(24, 0, 0, 0) - new Date()) / (1000 * 60 * 60)
  );
  const minutesRemaining = Math.floor(
    ((new Date().setHours(24, 0, 0, 0) - new Date()) % (1000 * 60 * 60)) /
      (1000 * 60)
  );
  const dealEndsIn =
    hoursRemaining > 0
      ? `${hoursRemaining} hours`
      : minutesRemaining < 5
      ? '5 minutes'
      : `${minutesRemaining} minutes`;

  const renderFeatureRow = (title, description) => {
    const popover = (
      <Popover
        id={`info-${title}`}
        style={{
          fontSize: '16px',
          fontFamily: 'Plus Jakarta Sans',
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: description }} />
      </Popover>
    );

    return (
      <div className={styles.featureRow}>
        <p
          className={styles.featureTitle}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <OverlayTrigger
          trigger={['hover', 'focus', 'click']}
          placement="left"
          overlay={popover}
          rootClose
        >
          <span>
            <SvgIcon icon="info_line" size="20" className={styles.infoIcon} />
          </span>
        </OverlayTrigger>
      </div>
    );
  };

  return (
    <VisibilitySensor
      partialVisibility
      delayedCall
      minTopValue={visible ? 0 : 100}
      active={!visible}
      onChange={(isVisible) => setVisible(isVisible)}
    >
      <div
        className={
          (gradient
            ? styles.gradient +
              ' ' +
              (gradient === 'left' ? styles.gradientLeft : styles.gradientRight)
            : styles.noGradient) + (className ? ' ' + className : '')
        }
        {...rest}
      >
        <div className={styles.pricingContainer}>
          <Grid>
            <Row className={styles.buttonRow}>
              <ButtonGroup
                bsSize="md"
                className={
                  'box-shadow ' +
                  styles.buttonGroup +
                  (showPaygOption ? ' ' + styles.buttonGroupWithPayg : '') +
                  (visible ? '' : ' ' + styles.hide)
                }
              >
                {showPaygOption && (
                  <Button
                    id="payg-billing-button"
                    bsStyle={billing === 'payg' ? 'info' : 'default'}
                    onClick={() => setBilling('payg')}
                    className={styles.buttonPayg}
                  >
                    Pay As You Go
                  </Button>
                )}
                <Button
                  id="monthly-billing-button"
                  bsStyle={billing === 'month' ? 'info' : 'default'}
                  onClick={() => setBilling('month')}
                  className={styles.buttonMonthly}
                >
                  Monthly plan
                </Button>
                <Button
                  id="annual-billing-button"
                  bsStyle={billing === 'year' ? 'info' : 'default'}
                  onClick={() => setBilling('year')}
                  className={styles.buttonYearly}
                >
                  Annual plan
                </Button>
              </ButtonGroup>
              <div
                className={
                  styles.annualPrice + (visible ? '' : ' ' + styles.hide)
                }
              >
                <div className={styles.annualPriceText}>
                  {isBlackFridayEligible(user)
                    ? '✨ 55% off annual, Black Friday Special!'
                    : '✨ 50% off annual, today only!'}
                </div>
                <SvgIcon
                  className={styles.annualPriceArrow}
                  icon="mail-forward"
                />
              </div>
              {billing === 'month' && hasCelloReferral && (
                <div
                  className={
                    styles.annualPrice + (visible ? '' : ' ' + styles.hide)
                  }
                >
                  <div className={styles.annualPriceText}>
                    🎁 15% referral discount applied at checkout
                  </div>
                </div>
              )}
            </Row>
            {isPaygBilling ? (
              <PaygPricingSection
                visible={visible}
                currency={currency}
                applicationCreditsPrice={applicationCreditsPrice}
                applicationCreditsPerApplication={
                  applicationCreditsPerApplication
                }
                proAnnualPerApplication={proAnnualPerApplication}
                proAnnualSavingsMultiplier={proAnnualSavingsMultiplier}
                loadingApplicationCreditsCheckout={
                  loadingApplicationCreditsCheckout
                }
                loadingPro={loadingPro}
                loadingPremium={loadingPremium}
                submitLoadingText={submitLoadingText}
                onBuyCredits={redirectToCreditsCheckout}
                onSeeAnnualPlans={() => setBilling('year')}
                renderFeatureRow={renderFeatureRow}
              />
            ) : (
              <Row className={styles.plansRow}>
                <Col
                  className={
                    styles.standardSection +
                    ' box-shadow' +
                    (visible ? '' : ' ' + styles.hide)
                  }
                  xs={12}
                  md={4}
                  mdPush={8}
                >
                  <Row>
                    <h3
                      className={
                        styles.header + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      Premium
                    </h3>
                  </Row>
                  <Row
                    className={
                      styles.section +
                      ' ' +
                      styles.priceSection +
                      (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    <p>
                      {billing === 'month' &&
                      (hasCelloReferral || isBlackFridayEligible(user)) ? (
                        <>
                          <span className="currency-symbol">
                            {currency.value}
                          </span>
                          <span
                            className={styles.price}
                            id="premium-price-daily"
                          >
                            {price.premium_month_daily}
                          </span>{' '}
                          <span className="billing-period">/ day</span>
                        </>
                      ) : (
                        <>
                          <span className="currency-symbol">
                            {currency.value}
                          </span>
                          <span
                            className={styles.price}
                            id="premium-price-daily"
                          >
                            {billing === 'year'
                              ? price.premium_year_month_daily
                              : price.premium_month_daily}
                          </span>{' '}
                          <span className="billing-period">/ day</span>
                        </>
                      )}
                    </p>
                    <p>
                      {billing === 'year' ? (
                        <span>
                          <span className={styles.strikethrough}>
                            {currency.value}
                            {price.premium_year_no_discount}
                          </span>
                          {` `}
                          {currency.value}
                          {price.premium_year} charged yearly.
                          <br />
                          {isBlackFridayEligible(user) ? (
                            <>
                              <span className={styles.promoDiscount}>
                                🔥&nbsp;&nbsp;55% off + price locked next year
                                {blackFridayCountdown && (
                                  <>
                                    <br />
                                    {blackFridayCountdown}
                                  </>
                                )}
                              </span>
                            </>
                          ) : (
                            <span className={styles.promoDiscount}>
                              🎉&nbsp;&nbsp;
                              <span className="hidden-md">Deal applied. </span>
                              Offer ends in {dealEndsIn}.
                            </span>
                          )}
                          <br />
                          You can cancel at any time.
                        </span>
                      ) : (
                        <span>
                          {hasCelloReferral ? (
                            <>
                              <span className={styles.strikethrough}>
                                {currency.value}
                                {price.premium_month_no_discount}
                              </span>
                              {` `}
                              {currency.value}
                              {price.premium_month} charged monthly.
                              <br />
                              <span className={styles.promoDiscount}>
                                🎉&nbsp;&nbsp;
                                <span className="hidden-md">
                                  Deal applied.{' '}
                                </span>
                                15% off monthly with your referral.
                              </span>
                            </>
                          ) : isBlackFridayEligible(user) ? (
                            <>
                              <span className={styles.strikethrough}>
                                {currency.value}
                                {price.premium_month_no_discount}
                              </span>
                              {` `}
                              {currency.value}
                              {price.premium_month} charged monthly.
                              <br />
                              <span className={styles.promoDiscount}>
                                🎉&nbsp;&nbsp;
                                <span className="hidden-md">
                                  Use code BLACKFRIDAY10
                                </span>
                              </span>
                            </>
                          ) : (
                            <span>
                              <span className="currency-symbol">
                                {currency.value}
                              </span>
                              {billing === 'year'
                                ? price.premium_year_month
                                : price.premium_month}{' '}
                              charged monthly.
                            </span>
                          )}
                          <br />
                          You can cancel at any time.
                        </span>
                      )}
                    </p>
                  </Row>
                  <Row
                    className={
                      styles.section +
                      ' ' +
                      styles.buttonSection +
                      (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {user && user.premium ? (
                      <div>
                        <p>You are already Premium</p>
                      </div>
                    ) : (
                      <div>
                        {user && user.subscribed && user.pro ? (
                          <Button
                            href={getSubscriptionUrl()}
                            className="btn-lg btn-block"
                          >
                            Upgrade to Premium
                          </Button>
                        ) : (
                          <ButtonWithLoader
                            loading={loadingPremium}
                            disabled={
                              loadingPro ||
                              loadingPremium ||
                              loadingApplicationCreditsCheckout
                            }
                            onClick={(event) => redirectToCheckout(event)}
                            className="btn-lg btn-block"
                            bsStyle="hollow"
                            id={premiumSubmitId}
                            text={
                              loadingPremium && submitLoadingText
                                ? submitLoadingText
                                : submitTextPremium
                            }
                            success={false}
                          />
                        )}
                      </div>
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'Apply to <b>unlimited jobs</b>',
                      'Unlimited applications each month and the ability to apply on any job.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      '<b>Premium</b> visibility to clients',
                      'Be featured at the top of client searches, above Pro freelancers.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'Job alerts',
                      'Instant notifications of new relevant jobs on Twine'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'Portfolio',
                      'Display your work on your portfolio to be viewed by clients.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'No transaction fees',
                      'By eliminating transaction fees, you can save a significant amount on your payouts.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'Ari · unlimited searches',
                      'An AI agent that finds freelance jobs across the web, matched to your portfolio.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'Pro badge',
                      'Stand out and increase your chance of being hired on jobs.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section +
                      (visible ? '' : ' ' + styles.hide + ' ' + styles.end)
                    }
                  >
                    {renderFeatureRow(
                      'Contact clients directly',
                      'Win more work with the direct email, WhatsApp or phone number of clients who share their details.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      '🌐 One-page website',
                      'A sleek, professional one-page website to link to your best work.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      '✨AI cover letter writer',
                      'A tool to help you writing cover letters, pitches and proposals.'
                    )}
                  </Row>
                </Col>
                <Col xs={12} md={4}>
                  <div
                    className={
                      styles.proSection +
                      ' box-shadow' +
                      (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    <Row className={styles.rowHeader}>
                      <h3
                        className={
                          styles.header +
                          ' ' +
                          styles.pro +
                          (visible ? '' : ' ' + styles.hide)
                        }
                      >
                        Pro
                      </h3>
                    </Row>
                    <Row
                      className={
                        styles.section +
                        ' ' +
                        styles.priceSection +
                        (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      <p>
                        {billing === 'month' &&
                        (hasCelloReferral || isBlackFridayEligible(user)) ? (
                          <>
                            <span className="currency-symbol">
                              {currency.value}
                            </span>
                            <span className={styles.price} id="pro-price-daily">
                              {price.month_daily}
                            </span>{' '}
                            <span className="billing-period">/ day</span>
                          </>
                        ) : (
                          <>
                            <span className="currency-symbol">
                              {currency.value}
                            </span>
                            <span className={styles.price} id="pro-price-daily">
                              {billing === 'year'
                                ? price.year_month_daily
                                : price.month_daily}
                            </span>{' '}
                            <span className="billing-period">/ day</span>
                          </>
                        )}
                      </p>
                      <p>
                        {billing === 'year' ? (
                          <span>
                            <span className={styles.strikethrough}>
                              {currency.value}
                              {price.year_no_discount}
                            </span>
                            {` `}
                            {currency.value}
                            {price.year} charged yearly.
                            <br />
                            {isBlackFridayEligible(user) ? (
                              <>
                                <span className={styles.promoDiscount}>
                                  🔥&nbsp;&nbsp;55% off + price locked for next
                                  year
                                  {blackFridayCountdown && (
                                    <>
                                      <br />
                                      {blackFridayCountdown}
                                    </>
                                  )}
                                </span>
                              </>
                            ) : (
                              <span className={styles.promoDiscount}>
                                🎉&nbsp;&nbsp;
                                <span className="hidden-md">
                                  Deal applied.{' '}
                                </span>
                                Offer ends in {dealEndsIn}.
                              </span>
                            )}
                            <br />
                            You can cancel at any time.
                          </span>
                        ) : (
                          <span>
                            {hasCelloReferral ? (
                              <>
                                <span className={styles.strikethrough}>
                                  {currency.value}
                                  {price.month_no_discount}
                                </span>
                                {` `}
                                {currency.value}
                                {price.month} charged monthly.
                                <br />
                                <span className={styles.promoDiscount}>
                                  🎉&nbsp;&nbsp;
                                  <span className="hidden-md">
                                    Deal applied.{' '}
                                  </span>
                                  15% off monthly with your referral.
                                </span>
                              </>
                            ) : isBlackFridayEligible(user) ? (
                              <>
                                <span className={styles.strikethrough}>
                                  {currency.value}
                                  {price.month_no_discount}
                                </span>
                                {` `}
                                {currency.value}
                                {price.month} charged monthly.
                                <br />
                                <span className={styles.promoDiscount}>
                                  🎉&nbsp;&nbsp;
                                  <span className="hidden-md">
                                    Use code BLACKFRIDAY10
                                  </span>
                                </span>
                              </>
                            ) : (
                              <span>
                                <span className="currency-symbol">
                                  {currency.value}
                                </span>
                                {billing === 'year'
                                  ? price.year_month
                                  : price.month}{' '}
                                charged monthly.
                              </span>
                            )}
                            <br />
                            You can cancel at any time.
                          </span>
                        )}
                      </p>
                    </Row>
                    <Row
                      className={
                        styles.section +
                        ' ' +
                        styles.buttonSection +
                        (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      {user && user.pro && !user.premium ? (
                        <div>
                          <p>You are already Pro</p>
                        </div>
                      ) : (
                        <div>
                          {user && user.subscribed && user.premium ? (
                            <Button
                              href={getSubscriptionUrl()}
                              bsStyle="hollow"
                              className="btn-lg btn-block"
                            >
                              Downgrade to Pro
                            </Button>
                          ) : (
                            <ButtonWithLoader
                              loading={loadingPro}
                              disabled={
                                loadingPro ||
                                loadingPremium ||
                                loadingApplicationCreditsCheckout
                              }
                              onClick={(event) => redirectToCheckout(event)}
                              bsStyle="primary"
                              className="btn-lg btn-block"
                              id={submitId}
                              text={
                                loadingPro && submitLoadingText
                                  ? submitLoadingText
                                  : submitTextPro
                              }
                              success={false}
                            />
                          )}
                        </div>
                      )}
                    </Row>
                    <Row
                      className={
                        styles.section + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      {renderFeatureRow(
                        'Apply to <b>50 jobs</b> per month',
                        '50 applications each month and the ability to apply on any job.'
                      )}
                    </Row>
                    <Row
                      className={
                        styles.section + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      {renderFeatureRow(
                        'More visibility to clients',
                        'Be featured above regular freelancers in client searches.'
                      )}
                    </Row>
                    <Row
                      className={
                        styles.section + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      {renderFeatureRow(
                        'Job alerts',
                        'Instant notifications of new relevant jobs on Twine'
                      )}
                    </Row>
                    <Row
                      className={
                        styles.section + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      {renderFeatureRow(
                        'Portfolio',
                        'Display your work on your portfolio to be viewed by clients.'
                      )}
                    </Row>
                    <Row
                      className={
                        styles.section + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      {renderFeatureRow(
                        'No transaction fees',
                        'By eliminating transaction fees, you can save a significant amount on your payouts.'
                      )}
                    </Row>
                    <Row
                      className={
                        styles.section + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      {renderFeatureRow(
                        'Ari · unlimited searches',
                        'An AI agent that finds freelance jobs across the web, matched to your portfolio.'
                      )}
                    </Row>
                    <Row
                      className={
                        styles.section + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      {renderFeatureRow(
                        'Pro badge',
                        'Stand out and increase your chance of being hired on jobs.'
                      )}
                    </Row>
                    <Row
                      className={
                        styles.section +
                        (visible ? '' : ' ' + styles.hide + ' ' + styles.end)
                      }
                    >
                      {renderFeatureRow(
                        'Contact clients directly',
                        'Win more work with the direct email, WhatsApp or phone number of clients who share their details.'
                      )}
                    </Row>
                    <Row
                      className={
                        styles.section + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      {renderFeatureRow(
                        '🌐 One-page website',
                        'A sleek, professional one-page website to link to your best work.'
                      )}
                    </Row>
                    <Row
                      className={
                        styles.section + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      {renderFeatureRow(
                        '✨AI cover letter writer',
                        'A tool to help you writing cover letters, pitches and proposals.'
                      )}
                    </Row>
                  </div>
                </Col>
                <Col
                  className={
                    styles.standardSection +
                    ' box-shadow' +
                    (visible ? '' : ' ' + styles.hide)
                  }
                  xs={12}
                  md={4}
                  mdPull={8}
                >
                  <Row>
                    <h3
                      className={
                        styles.header + (visible ? '' : ' ' + styles.hide)
                      }
                    >
                      Starter
                    </h3>
                  </Row>
                  <Row
                    className={
                      styles.section +
                      ' ' +
                      styles.priceSection +
                      (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    <p>
                      <span className="currency-symbol">{currency.value}</span>
                      <span className={styles.price} id="starter-price-daily">
                        0.00
                      </span>{' '}
                      <span className="billing-period">/ day</span>
                    </p>
                    <p className="">
                      Free standard account with job application limits.
                    </p>
                  </Row>
                  <Row
                    className={
                      styles.section +
                      ' ' +
                      styles.buttonSection +
                      (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    <Button
                      bsStyle="hollow"
                      className="btn-lg btn-block"
                      href={
                        user && user.subscribed
                          ? getSubscriptionUrl()
                          : user
                          ? '/'
                          : '/signup'
                      }
                    >
                      {user
                        ? user.pro
                          ? 'Downgrade to Free'
                          : 'Continue with Free'
                        : 'Sign up for free'}
                    </Button>
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'Apply to some jobs',
                      '1 application a day, for jobs with fewer applicants. Resets every 24 hours.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'Visible in client search',
                      "Be invited to jobs and appear in client's freelance search tools."
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'Job alerts',
                      'Instant notifications of new relevant jobs on Twine'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'Portfolio',
                      'Display your work on your portfolio to be viewed by clients.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section + (visible ? '' : ' ' + styles.hide)
                    }
                  >
                    {renderFeatureRow(
                      'Transaction fees',
                      'Transaction fees up to 6% of earnings depending on your payment method.'
                    )}
                  </Row>
                  <Row
                    className={
                      styles.section +
                      (visible ? '' : ' ' + styles.hide + ' ' + styles.end)
                    }
                  >
                    {renderFeatureRow(
                      'Ari · 3 searches a week',
                      'An AI agent that finds freelance jobs across the web, matched to your portfolio.'
                    )}
                  </Row>
                </Col>
              </Row>
            )}
            <Row className={styles.currencySelector}>
              <Col xs={12} sm={6} smPush={3} md={4} mdPush={4}>
                <div style={{ display: 'inline' }}>Billing currency: </div>
                <CurrencySelector
                  id="currency-selector"
                  value={currency}
                  style={{ width: '50%', display: 'inline-block' }}
                  onChange={(input) => setCurrency(input)}
                />
              </Col>
            </Row>
          </Grid>
        </div>
      </div>
    </VisibilitySensor>
  );
};

PricingBreakdownCreative.propTypes = {
  className: PropTypes.string,
  getSubscriptionToken: PropTypes.func,
  getApplicationCreditsCheckoutToken: PropTypes.func,
  gradient: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.oneOf(['left', 'right']),
  ]),
  subscriptionToken: PropTypes.string,
  applicationCreditsCheckoutToken: PropTypes.string,
  loadingApplicationCreditsCheckout: PropTypes.bool,
  applicationCreditsCheckoutError: PropTypes.object,
  submitId: PropTypes.string,
  submitTextPro: PropTypes.string,
  submitTextPremium: PropTypes.string,
  submitLoadingText: PropTypes.string,
  premiumSubmitId: PropTypes.string,
  user: PropTypes.object,
};

PricingBreakdownCreative.defaultProps = {
  gradient: false,
  submitId: 'pay-with-stripe-button',
  premiumSubmitId: 'pay-with-stripe-button-premium',
  submitTextPro: 'Get Pro',
  submitTextPremium: 'Get Premium',
  submitLoadingText: 'Loading...',
};

export default connect(
  (state) => ({
    subscriptionToken: state.payments.subscriptionToken,
    applicationCreditsCheckoutToken:
      state.payments.applicationCreditsCheckoutToken,
    loadingApplicationCreditsCheckout:
      state.payments.loadingApplicationCreditsCheckout,
    applicationCreditsCheckoutError:
      state.payments.applicationCreditsCheckoutError,
  }),
  {
    getSubscriptionToken,
    getApplicationCreditsCheckoutToken,
  }
)(PricingBreakdownCreative);
